import {Box, Button, Chip, Stack, Switch, Tooltip, Typography} from "@mui/material";
import {capitalize} from "@mui/material/utils";
import {Tool} from "../../api";

interface Props {
    tools: Tool[];
    activeIds: Set<string>;
    canEdit: boolean;
    onToggle: (toolId: string, active: boolean) => void;
    onConfigure?: (tool: Tool) => void;
    emptyLabel: string;
    configureLabel?: string;
    disabledIds?: Set<string>;
    disabledReason?: string;
}

export default function ToolListSection({tools, activeIds, canEdit, onToggle, onConfigure, emptyLabel, configureLabel, disabledIds, disabledReason}: Props) {
    if (tools.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyLabel}
            </Typography>
        );
    }

    return (
        <Stack spacing={1}>
            {tools.map(tool => {
                const isActive = activeIds.has(tool.id);
                const isToolDisabled = disabledIds?.has(tool.id) ?? false;
                const toggle = (
                    <Switch
                        id={`tool-toggle-${tool.id}`}
                        checked={isActive}
                        disabled={!canEdit || isToolDisabled}
                        onChange={(_, checked) => onToggle(tool.id, checked)}
                    />
                );
                return (
                    <Box
                        key={tool.id}
                        id={`tool-row-${tool.id}`}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{p: 1, borderRadius: 1, "&:hover": {bgcolor: "action.hover"}}}
                    >
                        {isToolDisabled && disabledReason ? (
                            <Tooltip title={disabledReason}>
                                <span>{toggle}</span>
                            </Tooltip>
                        ) : toggle}
                        <Box flexGrow={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">{tool.name}</Typography>
                                <Chip label={tool.stage} size="small" variant="outlined"/>
                            </Box>
                            {tool.description && (
                                <Typography variant="caption" color="text.secondary">
                                    {tool.description}
                                </Typography>
                            )}
                        </Box>
                        {onConfigure && (
                            <Button
                                id={`tool-configure-${tool.id}`}
                                size="small"
                                variant="outlined"
                                disabled={!isActive || !canEdit}
                                onClick={() => onConfigure(tool)}
                            >
                                {capitalize(configureLabel ?? "")}
                            </Button>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
}
